import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { usePostDetails } from '../../../../shared/hooks/usePostDetails';
import { ROUTES } from '../../../../core/constants/routes';
import ActivityDetails from '../../../../shared/components/ActivityDetails';
import { Text } from '../../../../core/design-system/components/Text';
import { Button } from '../../../../core/design-system/components/Button';
import { Icon } from '../../../../core/design-system/components/Icon';
import { Logo } from '../../../../core/design-system/components/Logo';
import ToastProvider from '../../../../core/providers/ToastProvider';
import { STORAGE_KEYS } from '../../../../core/constants/storage';
import './styles.css';

/**
 * PublicActivityPage - Page publique d'activité accessible sans authentification
 * Utilisé par GPT Store pour afficher les liens d'activités
 * RAS-41
 *
 * Wrappé dans ToastProvider car hors DashboardLayout
 */
const PublicActivityPageContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['activities', 'common', 'auth']);
  const { user } = useAuth();
  const { post, loading, error } = usePostDetails(id);

  // Si l'utilisateur est connecté, rediriger vers la page dashboard de l'activité
  useEffect(() => {
    if (!user || !id) return;

    const redirect = sessionStorage.getItem(STORAGE_KEYS.REDIRECT_AFTER_AUTH);
    if (redirect) {
      sessionStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_AUTH);
      navigate(redirect, { replace: true });
    } else {
      navigate(`${ROUTES.ACTIVITY}/${id}`, { replace: true });
    }
  }, [user, id, navigate]);

  const saveRedirectAndNavigate = (modal) => {
    // Sauvegarder l'URL de retour pour après auth
    sessionStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_AUTH, `${ROUTES.ACTIVITY}/${id}`);
    navigate(`/?modal=${modal}`);
  };

  if (loading) {
    return (
      <div className="public-activity-page">
        <div className="public-activity-loading">
          <Icon name="loader" size={32} className="public-activity-spinner" />
          <Text variant="body">{t('common:loading')}</Text>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="public-activity-page">
        <div className="public-activity-header">
          <Link to={ROUTES.HOME} className="public-activity-header-link" aria-label="Retour à l'accueil">
            <Logo size={32} />
            <span className="public-activity-brand">Raskmote</span>
          </Link>
        </div>
        <div className="public-activity-error">
          <Icon name="alert-triangle" size={48} />
          <Text variant="h3">
            {t('common:errors.not_found', 'Activité introuvable')}
          </Text>
          <Text variant="body" className="public-activity-error-sub">
            {t('activities:public.error_subtitle', "Cette activité n'existe plus ou le lien est invalide.")}
          </Text>
          <Button variant="primary" size="lg" onClick={() => navigate(ROUTES.HOME)}>
            {t('activities:public.discover_cta', 'Découvrir Raskmote')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-activity-page">
      <div className="public-activity-header">
        <Link to={ROUTES.HOME} className="public-activity-header-link" aria-label="Retour à l'accueil">
          <Logo size={32} />
          <span className="public-activity-brand">Raskmote</span>
        </Link>
      </div>

      <ActivityDetails
        activity={post}
        onJoin={!user ? () => saveRedirectAndNavigate('signup') : undefined}
        onBack={() => navigate(ROUTES.HOME)}
      />

      {/* CTA pour les utilisateurs non connectés */}
      {!user && (
        <div className="public-activity-cta">
          <Text variant="h3" className="public-activity-cta-title">
            {t('activities:public.join_title', 'Rejoignez cette activité')}
          </Text>
          <Text variant="body" className="public-activity-cta-sub">
            {t('activities:public.join_subtitle', 'Créez un compte gratuit pour participer et découvrir d\'autres activités près de chez vous.')}
          </Text>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => saveRedirectAndNavigate('signup')}
          >
            {t('auth:home.create_account', 'Créer un compte gratuitement')}
          </Button>
          <Button
            variant="outline-primary"
            size="lg"
            fullWidth
            onClick={() => saveRedirectAndNavigate('signin')}
            className="public-activity-cta-signin"
          >
            {t('auth:home.signin_section.button', 'Se connecter')}
          </Button>
        </div>
      )}
    </div>
  );
};

// Wrapper avec ToastProvider (nécessaire car hors DashboardLayout)
const PublicActivityPage = () => (
  <ToastProvider>
    <PublicActivityPageContent />
  </ToastProvider>
);

export default PublicActivityPage;
